<template>
  <div class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Audit Logs - {{ totalCount }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading Audit Logs..."
      @select="handleOrgSelect"
    />

    <!-- Error Message -->
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 mb-4">Loading audit logs...</div>

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search audit logs by object representation..."
          @input="debouncedFetchLogs"
        />
      </div>

      <!-- Date Range Filter -->
      <div class="flex items-center space-x-2">
        <input
          v-model="startDate"
          type="date"
          class="border border-gray-300 dark:border-gray-700 dark:bg-gray-700 rounded-md px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="fetchLogs"
        />
        <span>to</span>
        <input
          v-model="endDate"
          type="date"
          class="border border-gray-300 dark:border-gray-700 dark:bg-gray-700 rounded-md px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="fetchLogs"
        />
      </div>
    </div>

    <!-- Audit Logs Table -->
    <GenericTable
      v-if="!isLoading && !error"
      :columns="columns"
      :items="logs"
      itemKey="id"
      :selectable="false"
      @rowClick="showLogDetails"
      @sort="sortLogs"
    >
      <!-- Custom cell templates -->
      <template #timestamp="{ value }">
        {{ formatDateTime(value) }}
      </template>
      <template #action="{ value }">
        <span :class="getActionClass(value)">{{ getActionLabel(value) }}</span>
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      v-if="!isLoading && !error"
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="!hasPrevious"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="!hasNext"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          @change="updatePagination"
          :options="[{ id: 10, name: '10 per page' }, { id: 20, name: '20 per page' }, { id: 50, name: '50 per page' }, { id: 100, name: '100 per page' }]"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { getAuditLogsWithPagination } from "@/services/auditLogService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import { useRouter } from "vue-router";
import debounce from "lodash/debounce";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useStore } from "@/store";

export default {
  name: "AuditLogView",
  components: {
    SearchBar,
    GenericTable,
    GlassSelect,
    OrganizationTabs,
  },
  setup() {
    const router = useRouter();
    const tenant = useMultiTenant();
    const logs = ref([]);
    const searchQuery = ref("");
    const startDate = ref("");
    const endDate = ref("");
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    const error = ref(null);
    const isLoading = ref(false);
    const sortKey = ref("timestamp");
    const sortOrder = ref("desc");
    const totalCount = ref(0);
    const hasNext = ref(false);
    const hasPrevious = ref(false);
    const user = computed(() => useStore.getUser());
    const isServiceProviderUser = computed(() => useStore.isServiceProviderUser());

    const columns = [
      { key: "timestamp", label: "Timestamp", sortable: true, width: "20%" },
      { key: "object_repr", label: "Object", sortable: true, width: "30%" },
      { key: "action", label: "Action", sortable: true, width: "10%" },
      { key: "user", label: "User", sortable: true, width: "15%" },
      {
        key: "content_type",
        label: "Content Type",
        sortable: true,
        width: "25%",
      },
    ];

    const isFiltered = computed(() => {
      return (
        searchQuery.value !== "" ||
        startDate.value !== "" ||
        endDate.value !== ""
      );
    });

    const totalPages = computed(() => {
      return Math.ceil(totalCount.value / itemsPerPage.value);
    });

    const fetchLogs = async (schemaName = null) => {
      isLoading.value = true;
      try {
        error.value = null;
        const response = await getAuditLogsWithPagination({
          page: currentPage.value,
          pageSize: itemsPerPage.value,
          search: searchQuery.value,
          startDate: startDate.value,
          endDate: endDate.value,
          ordering: `${sortOrder.value === "desc" ? "-" : ""}${sortKey.value}`,
          schemaName: schemaName || tenant.currentSchemaName.value,
        });
        logs.value = response.results;
        totalCount.value = response.count;
        hasNext.value = !!response.next;
        hasPrevious.value = !!response.previous;
      } catch (err) {
        console.error("Error fetching audit logs:", err);
        error.value = "Failed to fetch audit logs. Please try again later.";
        logs.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const handleOrgSelect = async (orgId) => {
      currentPage.value = 1;
      await tenant.switchOrganization(orgId, fetchLogs);
    };

    const debouncedFetchLogs = debounce(fetchLogs, 300);

    const showLogDetails = (log) => {
      router.push({
        name: "AuditLogDetails",
        params: { id: log.id },
        query: tenant.addSchemaParam(),
      });
    };

    const changePage = (delta) => {
      currentPage.value += delta;
      fetchLogs();
    };

    const updatePagination = () => {
      currentPage.value = 1;
      fetchLogs();
    };

    const formatDateTime = (dateString) => {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const getActionClass = (action) => {
      switch (action) {
        case 0: // create
          return "text-green-600";
        case 1: // update
          return "text-blue-600";
        case 2: // delete
          return "text-red-600";
        default:
          return "text-gray-600";
      }
    };

    const sortLogs = (key) => {
      if (sortKey.value === key) {
        sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
      } else {
        sortKey.value = key;
        sortOrder.value = "asc";
      }
      fetchLogs();
    };

    watch([searchQuery, startDate, endDate], () => {
      currentPage.value = 1;
      debouncedFetchLogs();
    });

    onMounted(async () => {
      // If fetchOrganizations() already pinned the org (?client= on hard reload),
      // fetch directly - loadInitialData() would early-return without fetching.
      await tenant.fetchOrganizations();
      if (tenant.currentOrganizationId.value) {
        await tenant.syncUrlParam();
        await fetchLogs(tenant.currentSchemaName.value);
      } else {
        await tenant.loadInitialData(fetchLogs);
      }
    });

    const getActionLabel = (action) => {
      switch (action) {
        case 0:
          return "create";
        case 1:
          return "update";
        case 2:
          return "delete";
        case 3:
          return "access";
        default:
          return "unknown";
      }
    };

    return {
      logs,
      searchQuery,
      startDate,
      endDate,
      currentPage,
      itemsPerPage,
      columns,
      isFiltered,
      totalPages,
      totalCount,
      error,
      isLoading,
      hasNext,
      hasPrevious,
      showLogDetails,
      changePage,
      updatePagination,
      formatDateTime,
      getActionClass,
      sortLogs,
      getActionLabel,
      debouncedFetchLogs,
      tenant,
      handleOrgSelect,
      user,
      isServiceProviderUser,
    };
  },
};
</script>
